import { FormikErrors } from 'formik';
import { get } from 'lodash';
import { ParsedCard, ParsedForm } from 'store/declaration-form-errors/ParsedForm';
import { ICS2EnsDeclaration } from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { parseFieldErrorsForCard } from 'utils/validationParserUtils';
import {
    EMPTY_LEVEL_INDEX,
    emptyLevelMessage,
    parseContainerCard,
    parseCustomsOfficeCard,
    parseDangerousGoodsCard,
    parseGoodsInformationCard,
    parseGoodsItems,
    parseHouseConsignmentDetailsCard,
    parseHouseConsignmentPlacesCard,
    parseHouseConsignments,
    parseHouseTransportDocumentCard,
    parseEmptyLevels,
    parseMasterPlacesCard,
    parseMasterTransportDetailsCard,
    parseMasterTransportDocumentCard,
    parsePackagesCard,
    parsePartiesCard,
    parsePassiveBorderTransportMeansCard,
    parseSharedCards,
} from './ics2EnsValidationCards';

export const parseIcs2GoodsItemCards = (
    validation: any,
    { isMasterItem = false }: { isMasterItem?: boolean } = {}
): ParsedCard[] => {
    const errors: ParsedCard[] = [];
    if (!validation) return errors;

    parseGoodsInformationCard(validation, errors, isMasterItem ? 'Master Item Details' : 'Goods Information');

    parseDangerousGoodsCard(validation, errors);
    parsePackagesCard(validation, errors);
    parseSharedCards(validation, errors);

    return errors;
};

/** The cards of one house consignment, without its goods items. */
export const parseIcs2HouseConsignmentCards = (validation: any): ParsedCard[] => {
    const errors: ParsedCard[] = [];
    if (!validation) return errors;

    parseHouseConsignmentDetailsCard(validation, errors);

    parsePartiesCard(
        validation,
        [
            { path: 'consignor', label: 'Consignor' },
            { path: 'consignee', label: 'Consignee' },
            { path: 'notifyParty', label: 'Notify Party' },
            { path: 'goodsShipment.seller', label: 'Seller' },
            { path: 'goodsShipment.buyer', label: 'Buyer' },
        ],
        errors
    );

    parseFieldErrorsForCard(
        'Transport Details',
        [
            { 'transportCharges.methodOfPayment': 'Transport Method of Payment' },
            { 'countriesOfRoutingOfConsignment.0.country': 'Country of Routing of Consignment' },
            { countriesOfRoutingOfConsignment: 'Countries of Routing of Consignment' },
        ],
        errors,
        validation
    );

    // F11 declares the transport means the consignment moved on only on this level.
    parsePassiveBorderTransportMeansCard(validation, errors);

    parseHouseConsignmentPlacesCard(validation, errors);
    parseHouseTransportDocumentCard(validation, errors);
    parseContainerCard(validation, errors);
    parseSharedCards(validation, errors);

    return errors;
};

const MASTER_ITEMS_PATH = 'consignmentMasterLevel.goodsItem';
const HOUSE_CONSIGNMENTS_PATH = 'consignmentMasterLevel.consignmentHouseLevel';

const ics2EnsF11ValidationErrorsParser = (validation: FormikErrors<ICS2EnsDeclaration>): ParsedForm => {
    const masterDetails: ParsedCard[] = [];

    parseFieldErrorsForCard(
        'Master Details',
        [
            { lrn: 'LRN' },
            { 'documentIssueDate.dateTime': 'Document Issue Date' },
            { specificCircumstanceIndicator: 'Specific Circumstance Indicator' },
            { 'consignmentMasterLevel.referenceNumberUCR.referenceNumberUCR': 'Reference Number/UCR' },
            { 'consignmentMasterLevel.totalGrossMass': 'Total Gross Mass' },
        ],
        masterDetails,
        validation as any
    );

    parseFieldErrorsForCard(
        'Split Consignment',
        [
            { 'splitConsignment.splitConsignmentIndicator': 'Split Consignment Indicator' },
            { 'splitConsignment.previousMRN': 'Previous MRN' },
        ],
        masterDetails,
        validation as any
    );

    parsePartiesCard(
        validation,
        [
            { path: 'consignmentMasterLevel.consignor', label: 'Consignor' },
            { path: 'consignmentMasterLevel.consignee', label: 'Consignee' },
            { path: 'declarant', label: 'Declarant' },
            { path: 'representative', label: 'Representative' },
            { path: 'consignmentMasterLevel.carrier', label: 'Carrier' },
            { path: 'consignmentMasterLevel.notifyParty', label: 'Notify Party' },
        ],
        masterDetails
    );

    parseMasterTransportDetailsCard(validation, masterDetails);
    parseMasterPlacesCard(validation, masterDetails);
    parseCustomsOfficeCard(validation, masterDetails);

    parseFieldErrorsForCard(
        'Container',
        [{ 'consignmentMasterLevel.containerIndicator': 'Container Indicator' }],
        masterDetails,
        validation as any
    );

    parseMasterTransportDocumentCard(validation, masterDetails);
    parseSharedCards(validation, masterDetails, 'consignmentMasterLevel');

    parseEmptyLevels(validation, masterDetails, [
        { path: MASTER_ITEMS_PATH, name: 'Master Items' },
        { path: HOUSE_CONSIGNMENTS_PATH, name: 'House Consignments' },
    ]);

    const masterItems = parseGoodsItems(get(validation, MASTER_ITEMS_PATH), (item) =>
        parseIcs2GoodsItemCards(item, { isMasterItem: true })
    );
    const houseConsignments = parseHouseConsignments(
        get(validation, HOUSE_CONSIGNMENTS_PATH),
        parseIcs2HouseConsignmentCards,
        parseIcs2GoodsItemCards
    );

    // An empty level is marked as an entry numbered 0, which matches no item in the list but does
    // mark the tab of the level that has nothing in it.
    if (emptyLevelMessage(validation, MASTER_ITEMS_PATH)) {
        masterItems.push({ index: EMPTY_LEVEL_INDEX, errors: [] });
    }
    if (emptyLevelMessage(validation, HOUSE_CONSIGNMENTS_PATH)) {
        houseConsignments.push({ index: EMPTY_LEVEL_INDEX, errors: [], items: [] });
    }

    return {
        masterDetails,
        // ICS2 keeps its items apart: the single item list is what the other declaration types use.
        items: [],
        masterItems,
        houseConsignments,
    };
};

export default ics2EnsF11ValidationErrorsParser;
